import { NavLink, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import BottomNav from '@/components/layout/BottomNav';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { useDashboardMode, type DashboardMode } from '@/store/mode';
import { api } from '@/lib/api';
import { clearHistory } from '@/lib/tracking';

interface NavItem { label: string; href: string; icon: string; badge?: string }

/**
 * The two account types do genuinely different jobs, so they get different
 * menus rather than one menu with irrelevant entries greyed out:
 *
 *   SELLER - runs a shop. Needs "Sales" (orders waiting to be dispatched),
 *            "My listings" (inventory), offers, and Grow.
 *   BUYER  - shops. Needs "Orders & returns" (one place: a return is just a
 *            later stage of an order, not a separate concept) and Membership.
 *
 * Note that seller orders and buyer orders are different objects: a seller
 * fulfils, a buyer tracks and returns. Sharing one "Orders" screen between
 * them was the reason sellers had nowhere to see what needed dispatching.
 */
function sectionsFor(mode: DashboardMode): { title: string; items: NavItem[] }[] {
  const isSeller = mode === 'SELLER';

  if (isSeller) {
    return [
      {
        title: 'Store',
        items: [
          { label: 'Overview',        href: '/dashboard',           icon: 'home' },
          { label: 'Sales',           href: '/dashboard/sales',     icon: 'box' },
          { label: 'Returns',         href: '/dashboard/seller-returns', icon: 'refresh' },
          { label: 'My listings',     href: '/dashboard/listings',  icon: 'tag' },
          { label: 'Offers',          href: '/dashboard/offers',    icon: 'handshake' },
        ],
      },
      {
        title: 'Add stock',
        items: [
          { label: 'Sell new item',   href: '/dashboard/sell',      icon: 'plus' },
          { label: 'Swap a device',   href: '/dashboard/swap',      icon: 'swap' },
        ],
      },
      {
        title: 'Account',
        items: [
          { label: 'Grow your store', href: '/dashboard/grow',      icon: 'star', badge: 'NEW' },
          { label: 'Wallet & payouts',href: '/dashboard/wallet',    icon: 'wallet' },
          { label: 'Profile',         href: '/dashboard/profile',   icon: 'user' },
        ],
      },
    ];
  }

  return [
    {
      title: 'Buy',
      items: [
        { label: 'Overview',          href: '/dashboard',        icon: 'home' },
        { label: 'Orders & returns',  href: '/dashboard/orders', icon: 'box' },
      ],
    },
    {
      // Buyers don't list. Selling lives behind a seller account, and offering
      // it here only leads to a screen they can't complete.
      title: 'More',
      items: [
        { label: 'Swap a device',     href: '/dashboard/swap',   icon: 'swap' },
      ],
    },
    {
      title: 'Account',
      items: [
        { label: 'Membership',        href: '/dashboard/membership', icon: 'star' },
        { label: 'Wallet',            href: '/dashboard/wallet',     icon: 'wallet' },
        { label: 'Profile',           href: '/dashboard/profile',    icon: 'user' },
      ],
    },
  ];
}

export function DashboardLayout() {
  const { user, clear } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  /*
   * On phones the sidebar becomes a drawer.
   *
   * It used to be a permanently visible 260px column, which on a 390px screen
   * left ~130px for the actual page - every dashboard looked broken and half
   * the features were unreachable without pinch-zooming.
   */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const storedMode = useDashboardMode((s) => s.mode);
  const storedUserId = useDashboardMode((s) => s.userId);
  const setMode = useDashboardMode((s) => s.setMode);
  // Reloading the page keeps whichever view you were on. Defaults to Selling
  // only the first time (or for a different account on this browser) -
  // otherwise it stays wherever you last left it, in either direction.
  const mode: DashboardMode = (storedMode && storedUserId === user?.id) ? storedMode : 'SELLER';
  const sections = sectionsFor(mode);

  function switchMode(next: DashboardMode) {
    if (user?.id) setMode(next, user.id);
    navigate('/dashboard');
  }

  // Navigating from the drawer should close it, or you land on the new page
  // with the menu still covering it.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // A drawer that leaves the page scrolling behind it feels broken on iOS.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  async function logout() {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clear();
    // Wipe cached data/recommendations so the next account signed in on this
    // browser never sees a flash of this account's state.
    queryClient.clear();
    clearHistory();
    navigate('/login');
  }

  return (
    <div className="min-h-screen lg:flex bg-cream">
      {/* Mobile top bar - the only way into the menu on a phone. */}
      <header className="section-dark lg:hidden sticky top-0 z-40 flex items-center gap-3 px-4 h-14 bg-ink text-cream border-b border-white/10">
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="w-9 h-9 grid place-items-center rounded-lg hover:bg-white/10 transition"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <Link to="/" className="display text-gold text-xl leading-none">Souqli</Link>
        <Link to="/" className="ml-auto mono text-[10px] tracking-[0.12em] uppercase text-mute hover:text-gold-light transition shrink-0">← Marketplace</Link>
      </header>

      {/* Scrim behind the open drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-ink/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
      )}

      <aside
        className={`section-dark bg-ink text-cream flex flex-col transition-all duration-300 ease-souq
          fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shrink-0 lg:sticky lg:top-0 lg:h-screen lg:max-w-none ${
          collapsed ? 'lg:w-[78px]' : 'lg:w-[260px]'
        }`}
      >
        {/* Close control, drawer only */}
        <button
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
          className="lg:hidden absolute top-3 right-3 w-10 h-10 grid place-items-center rounded-lg text-mute hover:text-cream hover:bg-white/10"
        >
          ✕
        </button>
        <div className={`px-5 pt-6 pb-5 border-b border-white/5 flex items-center justify-between ${collapsed ? 'lg:justify-center' : ''}`}>
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shadow-glow shrink-0"
              style={{ background: 'linear-gradient(135deg,#E7A23E,#A66018)' }}
            >
              <span className="display text-ink text-xl font-bold leading-none">S</span>
            </div>
            <div className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <div className="display text-gold text-xl leading-none">Souqli</div>
              <div className="stamp text-mute mt-1 truncate">Member dashboard</div>
            </div>
          </Link>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              className="hidden lg:block text-mute hover:text-cream text-xs"
              aria-label="Collapse"
            >‹</button>
          )}
        </div>

        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="hidden lg:block text-mute hover:text-cream text-xs py-2"
            aria-label="Expand"
          >›</button>
        )}

        <nav className="flex-1 overflow-y-auto py-4 px-3 text-sm space-y-5">
          <Link
            to="/"
            title="Back to marketplace"
            className={`flex items-center gap-3 px-2.5 py-2 rounded-lg text-mute-soft hover:text-gold-light hover:bg-white/5 transition ${collapsed ? 'lg:justify-center' : ''}`}
          >
            <span className="text-base" aria-hidden>←</span>
            <span className={`${collapsed ? 'lg:hidden' : ''}`}>Back to marketplace</span>
          </Link>
          {user?.role === 'ADMIN' && (
            <Link
              to="/control-7f3a9b2c"
              title="Admin panel"
              className={`flex items-center gap-3 px-2.5 py-2 rounded-lg bg-gold/15 text-gold hover:bg-gold/25 transition ${collapsed ? 'lg:justify-center' : ''}`}
            >
              <span className="text-base">🛡️</span>
              <span className={`font-medium ${collapsed ? 'lg:hidden' : ''}`}>Admin panel</span>
            </Link>
          )}
          {sections.map((section) => (
            <div key={section.title}>
              <div className={`stamp text-gold/80 px-2 mb-2 ${collapsed ? 'lg:hidden' : ''}`}>{section.title}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === '/dashboard'}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-2.5 py-2 rounded-lg my-0.5 transition group ${
                      isActive ? 'text-gold bg-gold/10' : 'text-mute-soft hover:text-gold-light hover:bg-white/5'
                    } ${collapsed ? 'lg:justify-center' : ''}`
                  }
                >
                  <NavIcon name={item.icon} />
                  <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  {item.badge && <span className={`ml-auto pill pill-gold ${collapsed ? 'lg:hidden' : ''}`}>{item.badge}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={`p-4 pb-[calc(1rem+var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:pb-4 border-t border-white/5 ${collapsed ? 'lg:text-center' : ''}`}>
          <div className={collapsed ? 'lg:hidden' : ''}>
            <div className="stamp text-mute-soft">Signed in as</div>
            <div className="text-sm text-cream truncate">{user?.email}</div>
          </div>
          <button onClick={logout} className={`mt-3 text-xs text-gold hover:underline ${collapsed ? 'lg:mx-auto lg:block' : ''}`}>
            <span className={collapsed ? 'lg:hidden' : ''}>Log out</span>
            {collapsed && <span className="hidden lg:inline">⎋</span>}
          </button>
        </div>
      </aside>

      <BottomNav />
      <main className="flex-1 min-w-0 overflow-x-hidden has-bottom-nav">
        <div className="max-w-[1280px] mx-auto px-4 pt-4 sm:px-6 lg:px-10 lg:pt-6 flex justify-end">
          <div className="inline-flex bg-cream-deep border border-warmline rounded-full p-1 text-[12px]">
            <button
              onClick={() => switchMode('BUYER')}
              className={`px-3.5 py-1.5 rounded-full transition ${mode === 'BUYER' ? 'bg-ink text-cream font-medium' : 'text-mute hover:text-ink'}`}
            >
              Buyer
            </button>
            <button
              onClick={() => switchMode('SELLER')}
              className={`px-3.5 py-1.5 rounded-full transition ${mode === 'SELLER' ? 'bg-ink text-cream font-medium' : 'text-mute hover:text-ink'}`}
            >
              Seller
            </button>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-4 py-6 sm:px-6 lg:p-10 lg:pt-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavIcon({ name }: { name: string }) {
  const stroke = 'currentColor';
  const props = { className: 'w-4 h-4 shrink-0', fill: 'none', stroke, strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'home':     return <svg viewBox="0 0 24 24" {...props}><path d="M3 11 12 3l9 8" /><path d="M5 10v10h14V10" /></svg>;
    case 'box':      return <svg viewBox="0 0 24 24" {...props}><path d="M3 7l9-4 9 4-9 4-9-4z" /><path d="M3 7v10l9 4 9-4V7" /></svg>;
    case 'refresh':  return <svg viewBox="0 0 24 24" {...props}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 4v4h-4" /></svg>;
    case 'tag':      return <svg viewBox="0 0 24 24" {...props}><path d="M20 12 12 20l-9-9V3h8z" /><circle cx="7" cy="7" r="1.5" /></svg>;
    case 'handshake':return <svg viewBox="0 0 24 24" {...props}><path d="M3 12l4-4 5 5 5-5 4 4" /><path d="M12 13v6" /></svg>;
    case 'plus':     return <svg viewBox="0 0 24 24" {...props}><path d="M12 5v14M5 12h14" /></svg>;
    case 'swap':     return <svg viewBox="0 0 24 24" {...props}><path d="M3 7h14l-3-3M21 17H7l3 3" /></svg>;
    case 'gift':     return <svg viewBox="0 0 24 24" {...props}><rect x="3" y="8" width="18" height="13" rx="1.5" /><path d="M3 12h18M12 8v13M8 8c-2 0-3-3 0-3 2 0 4 3 4 3M16 8c2 0 3-3 0-3-2 0-4 3-4 3" /></svg>;
    case 'wallet':   return <svg viewBox="0 0 24 24" {...props}><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M16 13h2" /></svg>;
    case 'user':     return <svg viewBox="0 0 24 24" {...props}><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>;
    case 'star':     return <svg viewBox="0 0 24 24" {...props}><path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 18l-6.2 3.1 1.2-6.9-5-4.9 6.9-1z" /></svg>;
    default:         return <svg viewBox="0 0 24 24" {...props}><circle cx="12" cy="12" r="3" /></svg>;
  }
}
